/**
 * Drop jurisdictions from a started report via
 * POST /reports/{id}/jurisdictions/remove (admin).
 *
 * The Hub edit is saved first and then pushed to the report's Backoffice
 * project; if that push fails the person is told it saved but Backoffice is
 * behind. A refused removal (no reason, a subject left with none, a live
 * partner assignment covering it) is shown as the server's own message and
 * resolves to `null`.
 */

import { useCallback } from 'react';

import { notifications } from '@mantine/notifications';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import { transformToBackofficeSync } from '@/types/backoffice-sync';
import type { ReportResponse } from '@/types/report';
import { notifyIfBackofficeBehind } from '@/utils/backofficeSyncNotice';

export interface UseReportJurisdictionsResult {
  removeJurisdictions: (
    reportId: string,
    jurisdictionCodes: string[],
    reason: string
  ) => Promise<ReportResponse | null>;
}

export function useReportJurisdictions(): UseReportJurisdictionsResult {
  const removeJurisdictions = useCallback(
    async (reportId: string, jurisdictionCodes: string[], reason: string) => {
      try {
        const updated = await vnextApiClient.removeReportJurisdictions(
          reportId,
          jurisdictionCodes,
          reason
        );
        notifyIfBackofficeBehind(transformToBackofficeSync(updated.backoffice_sync));
        return updated;
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        Sentry.captureException(e);
        notifications.show({
          title: 'Could not remove jurisdiction',
          message: e.message,
          color: 'red',
        });
        return null;
      }
    },
    []
  );

  return { removeJurisdictions };
}
